import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// A lightweight overlay that drops a single camera's location on a map.
export default function CameraMapModal({ open, onClose, camera, lat, lng }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!open || lat == null || lng == null || !containerRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: true });
    map.setView([lat, lng], 16);
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19, attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    L.circleMarker([lat, lng], {
      radius: 9, color: '#fff', weight: 3, fillColor: '#dc2626', fillOpacity: 1,
    }).addTo(map).bindTooltip(camera, { permanent: false, direction: 'top' });
    mapRef.current = map;
    const t = setTimeout(() => { if (mapRef.current === map) map.invalidateSize(false); }, 60);
    return () => {
      clearTimeout(t);
      map.remove();
      mapRef.current = null;
    };
  }, [open, lat, lng, camera]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Box
      onClick={onClose}
      sx={{
        position: 'fixed', inset: 0, zIndex: 1300,
        bgcolor: 'rgba(15,23,42,.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2,
      }}
    >
      <Box
        onClick={e => e.stopPropagation()}
        sx={{
          bgcolor: '#fff', borderRadius: '14px', overflow: 'hidden', width: '100%', maxWidth: 640,
          boxShadow: '0 20px 60px rgba(0,0,0,.35)',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: '14px 18px', borderBottom: '1px solid #e2e8f0' }}>
          <Box sx={{ color: '#6d28d9', flexShrink: 0, mt: '2px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>{camera}</Typography>
            {lat != null && lng != null && (
              <Typography sx={{ fontSize: '11px', color: '#94a3b8', mt: '2px', fontFamily: "'Courier New', monospace" }}>
                {lat.toFixed(6)}, {lng.toFixed(6)}
              </Typography>
            )}
          </Box>
          <Box component="button" onClick={onClose}
            sx={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '22px', lineHeight: 1, p: '2px 6px', '&:hover': { color: '#dc2626' } }}>
            &times;
          </Box>
        </Box>

        {/* Map or no-GPS notice */}
        {lat != null && lng != null ? (
          <Box ref={containerRef} sx={{ height: 380, width: '100%', zIndex: 0 }} />
        ) : (
          <Box sx={{ p: '24px 18px' }}>
            <Typography sx={{ fontSize: '13px', color: '#92400e' }}>
              This read has no GPS coordinates, so its location can't be mapped.
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
